/**
 * Campo de comprobante de pago: DropZone (subir/arrastrar) + PEGAR imagen del portapapeles
 * (Ctrl/Cmd+V), muy común para capturas de Yape/transferencia. Al haber imagen, muestra una
 * vista previa grande (click para ampliar). El listener de pegar vive solo mientras el campo
 * está montado (el modal de pago lo monta al abrir).
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { DropZone, Box, BlockStack, InlineStack, Button, Text } from '@/lib/polaris';
import { ImageZoomModal } from '@/components/operations/ImageZoomModal';

/** Lo que dice el campo. Por defecto, el de un pago; la constancia de una agencia (20261021114500) dice lo suyo. */
export interface TextosDeLaCaptura {
  obligatoria: string;
  opcional: string;
  accion: string;
  pista: string;
  zoom: string;
}
const TEXTOS_DEL_PAGO: TextosDeLaCaptura = {
  obligatoria: 'Captura del pago · obligatoria · puedes pegarla con Ctrl/Cmd+V',
  opcional: 'Captura / comprobante (opcional) · puedes pegarla con Ctrl/Cmd+V',
  accion: 'Subir o pegar captura',
  pista: 'Foto del Yape / transferencia (o pega con Ctrl/Cmd+V)',
  zoom: 'Comprobante del pago',
};

export function PaymentProofField(
  { file, onChange, acciones, obligatorio = false, compacto = false, textos = TEXTOS_DEL_PAGO }: {
    /** Lo que dice el campo; por defecto, el de un pago. */
    textos?: TextosDeLaCaptura;
    file: File | null;
    onChange: (f: File | null) => void;
    acciones?: ReactNode;
    /** Una sola línea punteada (el modal de pago): sin el recuadro alto ni el texto de arriba. */
    compacto?: boolean;
    /**
     * 🪤 EN CASI TODAS LAS PANTALLAS LA CAPTURA ES OPCIONAL, y en Smarty Drop NO: ahí es lo único
     * que permite verificar un depósito de alguien que no está en tu equipo. El texto lo dice
     * según el caso en vez de mentir en uno de los dos.
     */
    obligatorio?: boolean;
  },
) {
  const [zoom, setZoom] = useState(false);
  // Object URL memoizado (se revoca al cambiar/desmontar para no filtrar memoria).
  // 🪤 Sólo un Blob de verdad: si llega otra cosa (un evento de clic, p. ej.), no se tumba el panel.
  const url = useMemo(() => (file instanceof Blob ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const it of Array.from(items)) {
        if (it.type.startsWith('image/')) {
          const f = it.getAsFile();
          if (f) { onChange(f); e.preventDefault(); }
          return;
        }
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [onChange]);

  return (
    <BlockStack gap="100">
      {compacto ? null : (
        <Text as="span" variant="bodySm" tone={obligatorio ? undefined : 'subdued'}>
          {obligatorio ? textos.obligatoria : textos.opcional}
        </Text>
      )}
      {/*
        🚨 CON ARCHIVO YA NO HAY ZONA DE ARRASTRE, y no es cosmético: era un bug.
        
        Antes la miniatura y sus botones vivían DENTRO del `DropZone`, que abre el selector de
        archivos con cualquier click en su superficie. Tocar «Rellenar con IA» abría el explorador de
        Windows en vez de leer la captura. Se podía tapar deteniendo la propagación en cada botón,
        pero eso deja la trampa armada para el próximo que agregue uno.
        
        La solución es que dejen de convivir: una zona que dice «soltá algo acá» no tiene sentido
        cuando ya soltaste algo. Con archivo es una FICHA —miniatura, nombre, acciones— como el
        adjunto de Gmail o de Slack. Y de paso se va el recuadro punteado, que ocupaba alto sin
        hacer nada.
      */}
      {file && url ? (
        <Box padding="200" borderWidth="025" borderColor="border" borderRadius="200">
          <InlineStack gap="300" blockAlign="center" wrap={false}>
            <button
              type="button" aria-label="Ampliar comprobante"
              onClick={() => setZoom(true)}
              style={{ all: 'unset', cursor: 'zoom-in', display: 'block', flexShrink: 0 }}
            >
              <img
                src={url} alt="Comprobante del pago"
                style={{ display: 'block', width: 48, height: 48, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--p-color-border)' }}
              />
            </button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <BlockStack gap="0">
                <Text as="span" variant="bodySm" truncate>{file.name || 'Captura pegada'}</Text>
                <Text as="span" variant="bodySm" tone="subdued">Click para ampliar</Text>
              </BlockStack>
            </div>
            {acciones}
            <Button variant="plain" tone="critical" onClick={() => onChange(null)}>Quitar</Button>
          </InlineStack>
        </Box>
      ) : compacto ? (
        <DropZone accept="image/*" type="image" allowMultiple={false} variableHeight outline={false} onDrop={(_d, accepted) => onChange(accepted[0] ?? null)}>
          <div style={{ border: '1px dashed #c1c9d2', borderRadius: 8, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10, background: '#fafbfc', cursor: 'pointer', fontSize: 13, color: '#697386' }}>
            <span aria-hidden="true">📎</span>
            <span><b style={{ color: '#1a1f36', fontWeight: 600 }}>Sube la captura del pago</b>{obligatorio ? ' · obligatoria' : ''} · arrástrala, haz clic o pégala con Ctrl/Cmd+V</span>
          </div>
        </DropZone>
      ) : (
        <DropZone accept="image/*" type="image" allowMultiple={false} onDrop={(_d, accepted) => onChange(accepted[0] ?? null)}>
          <DropZone.FileUpload actionTitle={textos.accion} actionHint={textos.pista} />
        </DropZone>
      )}
      <ImageZoomModal src={zoom ? url : null} alt={textos.zoom} onClose={() => setZoom(false)} />
    </BlockStack>
  );
}
